import classNames from "classnames";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useState } from "react";
import { v4 } from "uuid";
import Advertisement from "../../components/Advertisement";
import ArticlePush from "../../components/ArticleItem/ArticlePush";
import Container from "../../components/Container";
import PrimaryGrid from "../../components/Grid/PrimaryGrid";
import SecondaryGrid from "../../components/Grid/SecondaryGrid";
import { Media } from "../../components/Media";
import Navigation from "../../components/Navigation";
import StickySidebar from "../../components/StickySidebar";
import NativeFeedNewsDesktop from "../../components/pages/native/NativeFeedNewsDesktop";
import NativeFeedNewsMobile from "../../components/pages/native/NativeFeedNewsMobile";
import AdsIds from "../../data/ads";
import TransferWindowIds from "../../data/transferWindow";
import useCanonicalUrl from "../../hooks/useCanonicalUrl";
import { listByIds } from "../../services/api/ads";
import { getNewsList } from "../../services/api/news";
import { listTransferWindowById } from "../../services/api/transferWindow";
import { API } from "../../services/api/types";
import { AdsContext } from "../../utils/AdsContext";
import * as TransferWindow from "../../utils/TransferWindow";
import { DeviceTypeProvider } from "../../utils/deviceTypeContext";
import { createServerSidePropsError } from "../../utils/errorHandling/createServerSidePropsError";
import { logError } from "../../utils/errorHandling/logError";
import { DeviceType, getDeviceType } from "../../utils/getDeviceType";
import { shallowNavigate } from "../../utils/shallowNavigate";

const getNextRoute = (currentPath: string, page: number) => {
  if (currentPath.includes("?p"))
    return currentPath.replace(/\?p=\d+/, "?p=" + (page + 2));
  return currentPath + "?p=" + (page + 2);
};

const articlesPerPage = 20;

export const getServerSideProps: GetServerSideProps<{
  news?: API.Article[];
  page?: number;
  deviceType?: DeviceType;
  ads?: API.Ad[];
  transferWindow?: API.TransferWindow;
}> = async (context) => {
  const page = Number(context.query.p as string) - 1;

  try {
    const [news, ads, transferWindow] = await Promise.all([
      getNewsList({
        limit: articlesPerPage,
        offset: page ? page * articlesPerPage : 0,
        category: 24,
      }),
      listByIds([
        AdsIds.panorama_1,
        AdsIds.panorama_2,
        AdsIds.tws_1,
        AdsIds.tws_2,
        AdsIds.mpu,
        AdsIds.mobile_1,
        AdsIds.mobile_2,
        AdsIds.desktop_textlink_1,
        AdsIds.desktop_textlink_2,
        AdsIds.desktop_textlink_3,
        AdsIds.desktop_textlink_4,
        AdsIds.mobile_textlink_1,
        AdsIds.mobile_textlink_2,
        AdsIds.mobile_textlink_3,
        AdsIds.mobile_textlink_4,
        AdsIds.desktop_vi,
        AdsIds.mobile_vi,
        AdsIds.desktop_native,
        AdsIds.mobile_native,
        AdsIds.dynamic_mobile,
        AdsIds.dynamic_desktop,
        AdsIds.tws_insider,
      ]),
      listTransferWindowById(TransferWindowIds.swedish),
    ]);

    return {
      props: {
        news: news.data,
        page: page || 0,
        deviceType: getDeviceType(context),
        ads: ads.data,
        transferWindow: transferWindow.data,
      },
    };
  } catch (error: FixMeLater) {
    const errorId = v4();
    const isUserError =
      error.response?.status >= 400 && error.response?.status < 500;
    const response = createServerSidePropsError(
      isUserError ? 404 : 500,
      isUserError ? undefined : errorId
    );

    if (!isUserError) logError(error, errorId);

    context.res.statusCode = isUserError ? 404 : 500;

    return response;
  }
};

const IndexScreen: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const [articleIdToPage, setArticleIdToPage] = useState<{
    [key: number]: number;
  }>({});
  const [page, setPage] = useState(props.page!);
  const [news, setNews] = useState(props.news);
  const router = useRouter();
  const nextPageRoute = getNextRoute(router.asPath, page);

  const onReachEnd = async () => {
    const nextPage = page + 1;
    let offset = nextPage * articlesPerPage;
    const response = (
      await getNewsList({
        limit: 20,
        offset,
        category: 24,
      })
    ).data;
    setNews([...news!, ...response] as FixMeLater);
    setArticleIdToPage({
      ...articleIdToPage,
      [response[0].id]: nextPage,
    });
    setPage(nextPage);
  };

  const onArticleAppear = (
    article: API.Article,
    position: "below" | "above"
  ) => {
    const isBelow = position === "below";
    const page = articleIdToPage[article.id];

    if (!isBelow || !page) return;

    const newRoute = `${window.location.pathname}?p=${page + 1}`;

    shallowNavigate(newRoute);
  };

  const onArticleDisappear = (
    article: API.Article,
    position: "below" | "above"
  ) => {
    const isBelow = position === "below";
    const page = articleIdToPage[article.id];

    if (!isBelow || !page) return;

    const newRoute =
      page === 1
        ? `${window.location.pathname}`
        : `${window.location.pathname}?p=${page}`;

    shallowNavigate(newRoute);
  };

  const canonicalUrl = useCanonicalUrl();

  return (
    <AdsContext.Provider value={props.ads!}>
      <DeviceTypeProvider value={props.deviceType!}>
        <>
          <Head>
            <title>Native - sponsrade artiklar - Fotbolltransfers</title>
            <meta property="og:image" content={`/fb_share_ft.png`} />
            <link rel="canonical" href={canonicalUrl} />
          </Head>

          <Container>
            <Navigation active="news" />
          </Container>

          <a href={nextPageRoute} className={classNames("hidden")}>
            Nästa sida
          </a>

          <Container horizontalMargin>
            <PrimaryGrid
              top={
                <>
                  <Media greaterThanOrEqual="panorama">
                    <Advertisement id={AdsIds.panorama_1} />
                  </Media>
                  <Media lessThan="panorama">
                    <Advertisement id={AdsIds.mobile_1} />
                  </Media>
                </>
              }
              right={
                <StickySidebar>
                  <Advertisement id={AdsIds.tws_1} />
                  <div className={classNames("mb-5")} />
                  <Advertisement id={AdsIds.tws_2} />
                </StickySidebar>
              }
              main={
                <SecondaryGrid
                  left={
                    <>
                      <Media greaterThanOrEqual="md">
                        <NativeFeedNewsDesktop
                          onArticleAppear={onArticleAppear}
                          onArticleDisappear={onArticleDisappear}
                          onReachEnd={onReachEnd}
                          news={news!}
                        />
                      </Media>
                      <Media lessThan="md">
                        <NativeFeedNewsMobile
                          onArticleAppear={onArticleAppear}
                          onArticleDisappear={onArticleDisappear}
                          onReachEnd={onReachEnd}
                          news={news!}
                          transferWindow={props.transferWindow}
                        />
                      </Media>
                    </>
                  }
                  right={
                    <>
                      <div className={classNames(["my-5"])}>
                        <Advertisement id={AdsIds.tws_insider} />
                      </div>

                      <ArticlePush
                        href="/nyheter/nar-oppnar-och-stanger-transferfonstret/1468"
                        text="När öppnar och stänger transferfönstret? Se alla datum för 93 länder."
                        bubbleText={TransferWindow.getText(
                          new Date(),
                          new Date(props?.transferWindow?.open || 0),
                          new Date(props?.transferWindow?.close || 0),
                          TransferWindow.SWEDISH
                        )}
                        imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}/uploads/images/news/1580518483/528x300.jpg`}
                        imageAlt="Transferfönstret"
                      />

                      <div className={classNames(["mt-5"])} />
                      <Advertisement id={AdsIds.desktop_vi} />
                      <div className={classNames(["mb-5"])} />
                      <ArticlePush
                        href="/spelare/dyraste-spelarna"
                        text="Dyraste spelarna i Sverige och Europa - se hela listan!"
                        imageSrc="/push/expensive.jpg"
                        imageAlt="Spelare"
                      />
                      <div className={classNames(["mb-5"])} />
                      <ArticlePush
                        href="/nyheter/utbildningsbidrag-och-solidaritetsersattning/4896"
                        text="Allt du behöver veta om utbildningsbidrag och solidaritetsersättning."
                        imageSrc="/push/edu.jpg"
                        imageAlt="Spelare"
                      />
                    </>
                  }
                />
              }
            />
          </Container>
        </>
      </DeviceTypeProvider>
    </AdsContext.Provider>
  );
};

export default IndexScreen;
